import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { guideApi, GuideSection } from '../../api/client';
import { useApiQuery } from '../../hooks/useApiQuery';
import { Card, CardBody, ErrorAlert } from '../../components/ui';
import { BookOpen, Search, X, FileText, ArrowRight } from 'lucide-react';

// Tag color mapping
const tagColors: Record<string, string> = {
  'Getting Started': 'bg-green-100 text-green-700 border-green-200',
  'Tasks': 'bg-blue-100 text-blue-700 border-blue-200',
  'Credits': 'bg-amber-100 text-amber-700 border-amber-200',
  'FAQ': 'bg-purple-100 text-purple-700 border-purple-200',
  'Account': 'bg-pink-100 text-pink-700 border-pink-200',
  'Submissions': 'bg-cyan-100 text-cyan-700 border-cyan-200',
};

function getTagColor(tag: string): string {
  return tagColors[tag] || 'bg-gray-100 text-gray-700 border-gray-200';
}

// Format date
function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

// Guide Card Component
function GuideCard({ section }: { section: GuideSection }) {
  return (
    <Link to={`/guide/${section.id}`} className="block group">
      <Card className="h-full hover:shadow-lg transition-all duration-200 hover:-translate-y-0.5 border border-gray-200">
        <CardBody className="p-5">
          <div className="flex items-start gap-4">
            <div className="p-2.5 bg-primary-50 rounded-lg flex-shrink-0 group-hover:bg-primary-100 transition-colors">
              <FileText className="w-5 h-5 text-primary-600" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2">
                <h3 className="text-base font-semibold text-gray-900 truncate group-hover:text-primary-600 transition-colors">
                  {section.title}
                </h3>
                <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-primary-600 transition-colors flex-shrink-0" />
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                {section.tags.map((tag) => (
                  <span
                    key={tag}
                    className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full border ${getTagColor(tag)}`}
                  >
                    {tag}
                  </span>
                ))}
                <span className="text-xs text-gray-400 ml-auto">
                  Updated {formatDate(section.updated_at)}
                </span>
              </div>
            </div>
          </div>
        </CardBody>
      </Card>
    </Link>
  );
}

export function GuidePage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);

  // Debounced search value for API calls
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Debounce search input
  useMemo(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const { data, isLoading, isError, error, refetch } = useApiQuery({
    queryKey: ['guide-sections', debouncedSearch, selectedTags],
    queryFn: () => guideApi.getSections(debouncedSearch || undefined, selectedTags.length > 0 ? selectedTags : undefined),
    refetchOnMount: 'always',
  });

  const sections = data?.sections || [];
  const availableTags = data?.availableTags || [];

  const toggleTag = (tag: string) => {
    setSelectedTags(prev =>
      prev.includes(tag)
        ? prev.filter(t => t !== tag)
        : [...prev, tag]
    );
  };

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedTags([]);
  };

  const hasFilters = searchQuery.trim() !== '' || selectedTags.length > 0;

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <BookOpen className="w-8 h-8 text-primary-600" />
        <h1 className="text-2xl font-bold text-gray-900">User Guide</h1>
      </div>

      {/* Search and Filters */}
      <Card>
        <CardBody className="space-y-4">
          {/* Search Input */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search guides..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-10 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-shadow"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="w-4 h-4 text-gray-400" />
              </button>
            )}
          </div>

          {/* Tag Filters */}
          {availableTags.length > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-gray-500 mr-1">Tags:</span>
              {availableTags.map((tag) => (
                <button
                  key={tag}
                  onClick={() => toggleTag(tag)}
                  className={`inline-flex items-center px-3 py-1 text-sm font-medium rounded-full border transition-all ${
                    selectedTags.includes(tag)
                      ? getTagColor(tag)
                      : 'bg-white text-gray-600 border-gray-300 hover:border-gray-400'
                  }`}
                >
                  {tag}
                </button>
              ))}
              {hasFilters && (
                <button
                  onClick={clearFilters}
                  className="ml-2 text-sm text-primary-600 hover:text-primary-700 font-medium"
                >
                  Clear All
                </button>
              )}
            </div>
          )}
        </CardBody>
      </Card>

      {/* Results */}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      ) : isError ? (
        <ErrorAlert message={error?.message} onRetry={() => refetch()} />
      ) : sections.length === 0 ? (
        <Card>
          <CardBody className="py-12 text-center">
            <BookOpen className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            {hasFilters ? (
              <>
                <p className="text-gray-500">No guides found matching your search.</p>
                <button
                  onClick={clearFilters}
                  className="mt-3 text-sm text-primary-600 hover:text-primary-700 font-medium"
                >
                  Clear filters
                </button>
              </>
            ) : (
              <>
                <p className="text-gray-500">No guides available yet.</p>
                <p className="text-sm text-gray-400 mt-1">Please check back later.</p>
              </>
            )}
          </CardBody>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {sections.map((section) => (
            <GuideCard key={section.id} section={section} />
          ))}
        </div>
      )}
    </div>
  );
}
